import React, { useState, useEffect, useRef } from 'react';
import LeadService from '../../../services/LeadService';
import '../../../assets/css/GmailSetup.css';

const normalizeGroupSuggestions = (response) => {
  const payload = response?.data?.data ?? response?.data ?? [];
  if (!Array.isArray(payload)) return [];

  const groupsByKey = new Map();
  payload.forEach((group, index) => {
    const groupId = group?.groupId ?? group?.GroupId ?? group?.leadGroupId ?? group?.LeadGroupId ?? group?.id ?? group?.Id ?? '';
    const groupName = group?.groupName ?? group?.GroupName ?? group?.name ?? group?.Name ?? '';
    const leadItems = group?.associatedLeads ?? group?.AssociatedLeads ?? group?.leads ?? group?.Leads ?? group?.leadDetails ?? group?.LeadDetails ?? [];
    const normalizedLeads = (Array.isArray(leadItems) && leadItems.length > 0 ? leadItems : [group])
      .map((lead, leadIndex) => ({
        leadId: lead?.leadId ?? lead?.LeadId ?? lead?.id ?? lead?.Id ?? `${groupId || groupName || index}-lead-${leadIndex}`,
        leadName: lead?.leadName ?? lead?.LeadName ?? lead?.name ?? lead?.Name ?? '',
        email: lead?.email ?? lead?.Email ?? '',
      }))
      .filter((lead) => lead.email);

    if (!groupName || normalizedLeads.length === 0) return;

    const groupKey = String(groupId || groupName);
    const existing = groupsByKey.get(groupKey);
    if (existing) {
      const existingEmails = new Set(existing.leads.map((l) => l.email.toLowerCase()));
      normalizedLeads.forEach((lead) => {
        if (!existingEmails.has(lead.email.toLowerCase())) {
          existing.leads.push(lead);
          existingEmails.add(lead.email.toLowerCase());
        }
      });
      return;
    }
    groupsByKey.set(groupKey, { groupId: groupId || groupKey, groupName, leads: normalizedLeads });
  });

  return Array.from(groupsByKey.values());
};

const RecipientInput = ({ initialEmails = [], onChange }) => {
  const [inputValue, setInputValue] = useState('');
  const [emailTokens, setEmailTokens] = useState(initialEmails);
  const [groupTokens, setGroupTokens] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [groupLeadSearch, setGroupLeadSearch] = useState('');
  const requestIdRef = useRef(0);
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const groupEmails = groupTokens.flatMap((g) =>
      g.leads.filter((l) => g.selectedLeadIds.has(String(l.leadId))).map((l) => l.email)
    );
    const all = [...new Set([...emailTokens, ...groupEmails])];
    onChangeRef.current(all);
  }, [emailTokens, groupTokens]);

  useEffect(() => {
    const text = inputValue.trim();
    const reqId = ++requestIdRef.current;

    if (text.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await LeadService.getLeadAutoSuggestion(text);
        if (requestIdRef.current !== reqId) return;
        const groups = normalizeGroupSuggestions(res);
        const selectedIds = new Set(groupTokens.map((g) => String(g.groupId)));
        setSuggestions(groups.filter((g) => !selectedIds.has(String(g.groupId))));
        setShowSuggestions(true);
      } catch {
        if (requestIdRef.current !== reqId) return;
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        if (requestIdRef.current === reqId) setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, groupTokens]);

  const commitInputAsEmail = () => {
    const trimmed = inputValue.trim().toLowerCase();
    if (!trimmed) return;
    if (!emailTokens.includes(trimmed)) setEmailTokens((prev) => [...prev, trimmed]);
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
      e.preventDefault();
      commitInputAsEmail();
    } else if (e.key === 'Backspace' && !inputValue) {
      if (groupTokens.length > 0) {
        const last = groupTokens[groupTokens.length - 1];
        setGroupTokens((prev) => prev.slice(0, -1));
        if (String(activeGroupId) === String(last.groupId)) {
          setActiveGroupId(null);
          setGroupLeadSearch('');
        }
      } else if (emailTokens.length > 0) {
        setEmailTokens((prev) => prev.slice(0, -1));
      }
    }
  };

  const handleGroupSelect = (group) => {
    const selectedLeadIds = new Set(group.leads.map((l) => String(l.leadId)));
    setGroupTokens((prev) => [...prev, { ...group, selectedLeadIds }]);
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveGroupId(String(group.groupId));
    setGroupLeadSearch('');
  };

  const handleGroupRemove = (e, groupId) => {
    e.stopPropagation();
    setGroupTokens((prev) => prev.filter((g) => String(g.groupId) !== String(groupId)));
    if (String(activeGroupId) === String(groupId)) {
      setActiveGroupId(null);
      setGroupLeadSearch('');
    }
  };

  const handleLeadToggle = (groupId, leadId) => {
    setGroupTokens((prev) =>
      prev.map((g) => {
        if (String(g.groupId) !== String(groupId)) return g;
        const next = new Set(g.selectedLeadIds);
        const id = String(leadId);
        if (next.has(id)) next.delete(id); else next.add(id);
        return { ...g, selectedLeadIds: next };
      })
    );
  };

  const setAllLeads = (groupId, select) => {
    setGroupTokens((prev) =>
      prev.map((g) => {
        if (String(g.groupId) !== String(groupId)) return g;
        return { ...g, selectedLeadIds: select ? new Set(g.leads.map((l) => String(l.leadId))) : new Set() };
      })
    );
  };

  const activeGroup = groupTokens.find((g) => String(g.groupId) === String(activeGroupId));
  const filteredLeads = activeGroup?.leads.filter((l) =>
    !groupLeadSearch ||
    l.leadName?.toLowerCase().includes(groupLeadSearch.toLowerCase()) ||
    l.email?.toLowerCase().includes(groupLeadSearch.toLowerCase())
  ) ?? [];

  return (
    <div className="recipient-autocomplete compose-to">
      <div className="recipient-token-input" onClick={() => inputRef.current?.focus()}>
        {emailTokens.map((email) => (
          <button type="button" className="recipient-group-token" key={email}>
            <span>{email}</span>
            <span
              className="recipient-group-token-remove"
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setEmailTokens((prev) => prev.filter((t) => t !== email)); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setEmailTokens((prev) => prev.filter((t) => t !== email)); }}
            >×</span>
          </button>
        ))}
        {groupTokens.map((group) => (
          <button
            type="button"
            className={`recipient-group-token${String(activeGroupId) === String(group.groupId) ? ' active' : ''}`}
            key={group.groupId}
            onClick={(e) => {
              e.stopPropagation();
              setActiveGroupId(String(activeGroupId) === String(group.groupId) ? null : String(group.groupId));
              setGroupLeadSearch('');
            }}
          >
            <span>{group.groupName}</span>
            <span className="recipient-group-token-count">{group.selectedLeadIds.size}/{group.leads.length}</span>
            <span
              className="recipient-group-token-remove"
              role="button"
              tabIndex={0}
              onClick={(e) => handleGroupRemove(e, group.groupId)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleGroupRemove(e, group.groupId); }}
            >×</span>
          </button>
        ))}
        <input
          ref={inputRef}
          className="recipient-search-input"
          type="text"
          placeholder={emailTokens.length + groupTokens.length > 0 ? 'Add more...' : 'Search leads or type email'}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { commitInputAsEmail(); setTimeout(() => setShowSuggestions(false), 150); }}
          onFocus={() => { if (inputValue.trim().length >= 3) setShowSuggestions(true); }}
          autoComplete="off"
        />
      </div>

      {showSuggestions && (suggestions.length > 0 || loading) && (
        <div className="lead-suggestions-dropdown">
          {loading ? (
            <div className="lead-suggestion-status">Searching...</div>
          ) : (
            suggestions.map((s) => (
              <button
                type="button"
                className="lead-suggestion-item"
                key={s.groupId}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleGroupSelect(s)}
              >
                <span className="lead-suggestion-name">{s.groupName}</span>
                <span className="lead-suggestion-email">{s.leads.length} leads</span>
              </button>
            ))
          )}
        </div>
      )}

      {activeGroup && (
        <div className="group-leads-panel">
          <div className="group-leads-panel-header">
            <div>
              <strong>{activeGroup.groupName}</strong>
              <span>{activeGroup.selectedLeadIds.size} of {activeGroup.leads.length} selected</span>
            </div>
            <button type="button" onClick={() => { setActiveGroupId(null); setGroupLeadSearch(''); }}>×</button>
          </div>
          <input
            className="group-lead-search"
            type="text"
            placeholder="Search leads"
            value={groupLeadSearch}
            onChange={(e) => setGroupLeadSearch(e.target.value)}
          />
          <div className="group-leads-actions">
            <button type="button" onClick={() => setAllLeads(activeGroup.groupId, true)}>Select all</button>
            <button type="button" onClick={() => setAllLeads(activeGroup.groupId, false)}>Deselect all</button>
          </div>
          <div className="group-leads-list">
            {filteredLeads.length > 0 ? filteredLeads.map((lead) => (
              <label className="group-lead-option" key={`${activeGroup.groupId}-${lead.leadId}`}>
                <input
                  type="checkbox"
                  checked={activeGroup.selectedLeadIds.has(String(lead.leadId))}
                  onChange={() => handleLeadToggle(activeGroup.groupId, lead.leadId)}
                />
                <span>
                  <strong>{lead.leadName || lead.email}</strong>
                  <small>{lead.email}</small>
                </span>
              </label>
            )) : (
              <div className="lead-suggestion-status">No leads found.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipientInput;
